/**
 * Predicates for checking an AI search prompt and its answer against live inventory. The AI index is
 * rebuilt daily and reports a sold vehicle as `ACTIVE` with its real `stockNumber`, so only a live
 * lookup identifies one.
 */

/** The 17-character alphanumeric VIN shape. */
const VIN_PATTERN = /^[0-9A-Za-z]{17}$/;

/**
 * Whether a prompt is a bare VIN. Deliberately identical to the producer's own
 * `Joi.string().alphanum().length(17)` rule, which gates its VIN lookup.
 */
export const isVinLike = (value?: string): boolean => VIN_PATTERN.test(value?.trim() ?? '');

/**
 * Whether two vins name the same vehicle. Trimmed and case-insensitive, because the values come from
 * different places: one is user-typed, the other an upstream payload or a stored prompt. Absent on
 * either side is never a match.
 */
export const isSameVin = (first?: string, second?: string): boolean => {
    if (!first || !second) {
        return false;
    }

    return first.trim().toUpperCase() === second.trim().toUpperCase();
};

type SearchResultVehicle = { stockNumber?: string };

/**
 * Whether a `/v2/vehicle/search` response puts the VIN in active inventory. Only inventory records
 * carry a `stockNumber`; the count does not distinguish them, because the endpoint falls back to a
 * catalog decode. Same predicate `VehicleListRowItem.interstate.tsx:15` badges on.
 */
export const isVinInActiveInventory = (vehicles?: SearchResultVehicle[]): boolean =>
    Boolean(vehicles?.some((vehicle) => Boolean(vehicle.stockNumber)));

/**
 * Clears the stock number on rows for a vin live inventory no longer holds. That is the field the row
 * component picks its variant from and the results alert keys on
 * (`VehicleListRowItem.interstate.tsx:15`), so nothing else has to change. Every other row is returned
 * untouched, as is the array itself when no row matches. Case-insensitive: one side is an upstream
 * payload, the other is user-typed.
 */
export const markOutOfStockByVin = <T extends { vin?: string; stockNumber?: string }>(vehicles: T[], vin?: string): T[] => {
    if (!vin) {
        return vehicles;
    }

    const target = vin.trim().toUpperCase();
    const isTarget = (vehicle: T) => vehicle.vin?.trim().toUpperCase() === target;

    if (!vehicles.some(isTarget)) {
        return vehicles;
    }

    return vehicles.map((vehicle) => (isTarget(vehicle) ? { ...vehicle, stockNumber: undefined } : vehicle));
};
